/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { supabase } from "../config/supabase";
import { generateEmpresaSlug } from "../utils/slugUtils";
import { generateProtocolNumber, hashCpfWithPepper, isValidCpf } from "../utils/security";
import ConfirmacaoCandidaturaModal from "../components/ConfirmacaoCandidaturaModal";

interface EleicaoInfo {
  id: string;
  titulo: string;
  empresa_nome: string;
  empresa_slug: string;
  status: string;
  cadastro_publico_habilitado: boolean;
  votacao_ativa?: boolean;
  usuario_id: string;
  empresa_id: string;
}

const CadastroCandidatoPublico: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [eleicaoInfo, setEleicaoInfo] = useState<EleicaoInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [mostrarModalConfirmacao, setMostrarModalConfirmacao] = useState(false);
  const [dadosConfirmacao, setDadosConfirmacao] = useState<{
    protocolo: string;
    dataRegistro: string;
    horaRegistro: string;
    cpf: string;
    nome: string;
    setor: string;
    funcao: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    matricula: "",
    setor: "",
    cargo: "",
    endereco: "",
    cidade: "",
    estado: "",
    cep: "",
    observacoes: "",
    cpf: "",
  });

  const [foto, setFoto] = useState<File | null>(null);
  const [previewFoto, setPreviewFoto] = useState<string | null>(null);

  // Carregar informações da eleição
  useEffect(() => {
    const carregarEleicao = async () => {
      if (!slug) {
        setError("URL inválida");
        setLoading(false);
        return;
      }

      try {
        console.log("🔍 [CADASTRO PÚBLICO] Buscando eleição com slug:", slug);

        // Status que permitem cadastro de candidatos
        // configurando: eleição está sendo configurada, cadastro habilitado
        // cadastro_candidatos: fase específica de cadastro de candidatos
        // ativa: eleição ativa que pode estar em fase de cadastro
        const statusPermitidos = [
          "configurando",
          "cadastro_candidatos",
          "ativa",
        ];

        // Primeiro, tentar buscar pela empresa_slug exata (pegar a mais recente)
        let { data: eleicoes, error } = await supabase
          .from("cp_eleicoes")
          .select(
            "id, titulo, empresa_nome, empresa_slug, status, cadastro_publico_habilitado, votacao_ativa, usuario_id, empresa_id",
          )
          .eq("empresa_slug", slug)
          .in("status", statusPermitidos)
          .order("created_at", { ascending: false })
          .limit(1);

        // Pegar a primeira eleição (mais recente)
        let eleicao = eleicoes && eleicoes.length > 0 ? eleicoes[0] : null;

        console.log("🔍 [CADASTRO PÚBLICO] Resultado da busca por slug:", {
          eleicoes,
          eleicao,
          error,
          totalEncontradas: eleicoes ? eleicoes.length : 0,
        });

        // Se não encontrar, tentar buscar por nome da empresa (para compatibilidade)
        if (!eleicao) {
          console.log(
            "🔍 [CADASTRO PÚBLICO] Tentando buscar por nome da empresa:",
            slug,
          );
          const { data: eleicoesPorNome, error: errorPorNome } = await supabase
            .from("cp_eleicoes")
            .select(
              "id, titulo, empresa_nome, empresa_slug, status, cadastro_publico_habilitado, votacao_ativa, usuario_id, empresa_id",
            )
            .eq("empresa_nome", slug)
            .in("status", statusPermitidos)
            .order("created_at", { ascending: false })
            .limit(1);

          console.log("🔍 [CADASTRO PÚBLICO] Resultado da busca por nome:", {
            eleicoesPorNome,
            errorPorNome,
          });

          if (!errorPorNome && eleicoesPorNome && eleicoesPorNome.length > 0) {
            eleicao = eleicoesPorNome[0];
            error = null;
          }
        }

        // Verificar se encontrou a eleição
        if (!eleicao) {
          console.error("❌ [CADASTRO PÚBLICO] Nenhuma eleição encontrada");
          setError(
            "Nenhuma eleição ativa encontrada para este link. Verifique se a eleição existe e está ativa.",
          );
          return;
        }

        console.log("✅ [CADASTRO PÚBLICO] Eleição encontrada:", eleicao);
        console.log("✅ [CADASTRO PÚBLICO] IDs da eleição:", {
          usuario_id: eleicao.usuario_id,
          empresa_id: eleicao.empresa_id,
        });

        // Verificar se o cadastro público está habilitado
        if (!eleicao.cadastro_publico_habilitado) {
          setError(
            "O cadastro público de candidatos não está habilitado para esta eleição. Entre em contato com o administrador.",
          );
          return;
        }

        setEleicaoInfo(eleicao);
        console.log("✅ [CADASTRO PÚBLICO] Eleição carregada com sucesso");
      } catch (err) {
        console.error(
          "❌ [CADASTRO PÚBLICO] Erro inesperado ao carregar eleição:",
          err,
        );
        setError(
          "Erro inesperado ao carregar informações da eleição. Tente novamente.",
        );
      } finally {
        setLoading(false);
      }
    };

    carregarEleicao();
  }, [slug]);

  // Função para lidar com seleção de foto
  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith("image/")) {
        setError(
          "Por favor, selecione apenas arquivos de imagem (JPG, PNG, etc.)",
        );
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("A foto deve ter no máximo 5MB");
        return;
      }

      setFoto(file);

      // Criar preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewFoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Função para salvar foto localmente
  const salvarFotoLocalmente = async (
    foto: File,
    matricula: string,
  ): Promise<string> => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = () => {
        const base64 = reader.result as string;
        const storageKey = `foto_candidato_${matricula}_${Date.now()}`;
        localStorage.setItem(storageKey, base64);
        resolve(storageKey);
      };
      reader.onerror = reject;
      reader.readAsDataURL(foto);
    });
  };

  // Função para submeter o formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!eleicaoInfo) {
      setError("Informações da eleição não carregadas");
      return;
    }

    // Validações básicas
    if (
      !formData.nome ||
      !formData.email ||
      !formData.matricula ||
      !formData.setor
    ) {
      setError("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Por favor, insira um email válido");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      let fotoUrl: string | undefined;

      // Se há foto, salvar localmente
      if (foto) {
        fotoUrl = await salvarFotoLocalmente(foto, formData.matricula);
      }

      // VALIDAÇÃO REFORÇADA DE DUPLICATAS
      // 1. Verificar se já existe candidato com a mesma matrícula
      const { data: candidatosExistentes, error: checkError } = await supabase
        .from("cp_candidatos")
        .select("id, nome, matricula, setor, email")
        .eq("eleicao_id", eleicaoInfo.id)
        .eq("ativo", true);

      if (checkError && checkError.code !== "PGRST116") {
        throw checkError;
      }

      if (candidatosExistentes && candidatosExistentes.length > 0) {
        // Verificar duplicata por matrícula (mais específico)
        const duplicadoPorMatricula = candidatosExistentes.find(
          c => c.matricula === formData.matricula
        );
        
        if (duplicadoPorMatricula) {
          setError(
            `Já existe um candidato cadastrado com a matrícula ${formData.matricula} nesta eleição.`
          );
          return;
        }

        // Verificar duplicata por nome + setor (para evitar mesma pessoa com matrícula diferente)
        const duplicadoPorNomeSetor = candidatosExistentes.find(
          c => c.nome.toLowerCase() === formData.nome.toLowerCase() && 
               c.setor.toLowerCase() === formData.setor.toLowerCase()
        );
        
        if (duplicadoPorNomeSetor) {
          setError(
            `Já existe um candidato com o nome "${formData.nome}" no setor "${formData.setor}". ` +
            `Se você já se cadastrou, não é necessário fazer novamente.`
          );
          return;
        }

        // Verificar duplicata por email (único por eleição)
        const duplicadoPorEmail = candidatosExistentes.find(
          c => c.email && c.email.toLowerCase() === formData.email.toLowerCase()
        );
        
        if (duplicadoPorEmail) {
          setError(
            `Este email já está cadastrado para outro candidato nesta eleição.`
          );
          return;
        }

        // Verificar duplicata por CPF (se informado e válido)
        if (formData.cpf) {
          const cpfLimpo = formData.cpf.replace(/\D/g, "");
          if (cpfLimpo.length === 11 && isValidCpf(cpfLimpo)) {
            // Gerar hash do CPF para comparação segura (LGPD)
            const cpfHash = await hashCpfWithPepper(cpfLimpo, eleicaoInfo.id);
            
            // Buscar se já existe candidato com este CPF hash
            const { data: candidatoComCpf } = await supabase
              .from("cp_candidatos")
              .select("id")
              .eq("eleicao_id", eleicaoInfo.id)
              .eq("cpf_hash", cpfHash)
              .eq("ativo", true)
              .single();
            
            if (candidatoComCpf) {
              setError(
                `Este CPF já está cadastrado como candidato nesta eleição.`
              );
              return;
            }
          }
        }
      }

      // Gerar hash do CPF para armazenamento seguro (LGPD)
      let cpfHash: string | undefined;
      if (formData.cpf) {
        const cpfLimpo = formData.cpf.replace(/\D/g, "");
        if (cpfLimpo.length === 11 && isValidCpf(cpfLimpo)) {
          cpfHash = await hashCpfWithPepper(cpfLimpo, eleicaoInfo.id);
        }
      }

      // Dados para inserção
      const dadosCandidato = {
        eleicao_id: eleicaoInfo.id,
        usuario_id: eleicaoInfo.usuario_id,
        empresa_id: eleicaoInfo.empresa_id,
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        matricula: formData.matricula,
        cpf_hash: cpfHash,
        setor: formData.setor,
        cargo: formData.cargo,
        endereco: formData.endereco,
        cidade: formData.cidade,
        estado: formData.estado,
        cep: formData.cep,
        observacoes: formData.observacoes,
        votos: 0,
        foto_url: fotoUrl,
        ativo: true,
      };

      console.log("🔍 [CADASTRO PÚBLICO] Dados para inserção:", dadosCandidato);

      // Inserir candidato
      const { data: novoCandidato, error: insertError } = await supabase
        .from("cp_candidatos")
        .insert(dadosCandidato)
        .select()
        .single();

      if (insertError) {
        console.error("Erro ao inserir candidato:", insertError);
        throw insertError;
      }

      console.log("✅ Candidato cadastrado com sucesso:", novoCandidato);

      // Gerar protocolo e exibir modal de confirmação
      // Guardar os dados ANTES de limpar o formulário
      const protocolo = generateProtocolNumber();
      const agora = new Date();
      const dadosParaConfirmacao = {
        protocolo,
        dataRegistro: agora.toISOString().split("T")[0],
        horaRegistro: agora.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        cpf: formData.cpf || formData.matricula,
        nome: formData.nome,
        setor: formData.setor,
        funcao: formData.cargo,
      };

      setDadosConfirmacao(dadosParaConfirmacao);
      setMostrarModalConfirmacao(true);
      // NÃO setSuccess(true) aqui - será setado quando o modal fechar
      // Isso garante que o usuário veja o modal de confirmação com protocolo primeiro

      // Limpar formulário
      setFormData({
        nome: "",
        email: "",
        telefone: "",
        matricula: "",
        setor: "",
        cargo: "",
        endereco: "",
        cidade: "",
        estado: "",
        cep: "",
        observacoes: "",
        cpf: "",
      });
      setFoto(null);
      setPreviewFoto(null);
    } catch (err) {
      console.error("Erro ao cadastrar candidato:", err);
      setError("Erro ao cadastrar candidato. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando informações da eleição...</p>
        </div>
      </div>
    );
  }

  if (error && !eleicaoInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-lg mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Erro ao Acessar Eleição
          </h1>
          <p className="text-gray-600 mb-4">{error}</p>

          {/* Informações adicionais para debug */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-left">
            <h3 className="font-medium text-yellow-800 mb-2">
              ℹ️ Informações para o Administrador:
            </h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• URL acessada: /candidato/{slug}</li>
              <li>• Verifique se a eleição existe no sistema</li>
              <li>• Confirme se o status da eleição está como "ativa"</li>
              <li>• Verifique se o cadastro público está habilitado</li>
              <li>• Confirme se o slug da empresa está correto</li>
            </ul>
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate("/")}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Voltar ao Início
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Cadastro Realizado!
          </h1>
          <p className="text-gray-600 mb-4">
            Seu cadastro como candidato foi realizado com sucesso na eleição:
          </p>
          <p className="text-lg font-semibold text-blue-600 mb-4">
            {eleicaoInfo?.titulo}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Você receberá mais informações sobre a eleição em breve.
          </p>
          <button
            onClick={() => setSuccess(false)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Fazer Novo Cadastro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Cabeçalho */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Building className="w-12 h-12 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">
              Cadastro de Candidato
            </h1>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h2 className="text-xl font-semibold text-blue-800 mb-2">
              {eleicaoInfo?.titulo}
            </h2>
            <p className="text-blue-700 mb-3">
              <Building className="w-4 h-4 inline mr-2" />
              {eleicaoInfo?.empresa_nome}
            </p>

            {/* Status da votação */}
            <div className="bg-white border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <div
                  className={`w-3 h-3 rounded-full ${eleicaoInfo?.votacao_ativa ? "bg-green-500" : "bg-yellow-500"}`}
                ></div>
                <span className="font-medium text-gray-800">
                  {eleicaoInfo?.votacao_ativa
                    ? "🗳️ Votação Ativa"
                    : "⏳ Votação em Preparação"}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                {eleicaoInfo?.votacao_ativa
                  ? "A votação está em andamento. Candidatos podem se cadastrar e eleitores podem votar."
                  : "A eleição está sendo preparada. Candidatos podem se cadastrar, mas a votação ainda não foi iniciada."}
              </p>
            </div>
          </div>
        </div>

        {/* Formulário */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dados Pessoais */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Dados Pessoais
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label
                    htmlFor="nome"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    required
                    value={formData.nome}
                    onChange={(e) =>
                      setFormData({ ...formData, nome: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Seu nome completo"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Telefone
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) =>
                      setFormData({ ...formData, telefone: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
            </div>

            {/* Dados Profissionais */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-600" />
                Dados Profissionais
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="matricula"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Matrícula *
                  </label>
                  <input
                    type="text"
                    id="matricula"
                    required
                    value={formData.matricula}
                    onChange={(e) =>
                      setFormData({ ...formData, matricula: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Sua matrícula"
                  />
                </div>

                <div>
                  <label
                    htmlFor="setor"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Setor *
                  </label>
                  <input
                    type="text"
                    id="setor"
                    required
                    value={formData.setor}
                    onChange={(e) =>
                      setFormData({ ...formData, setor: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Seu setor"
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor="cargo"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Cargo
                  </label>
                  <input
                    type="text"
                    id="cargo"
                    value={formData.cargo}
                    onChange={(e) =>
                      setFormData({ ...formData, cargo: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Seu cargo atual"
                  />
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Endereço
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label
                    htmlFor="endereco"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Endereço
                  </label>
                  <input
                    type="text"
                    id="endereco"
                    value={formData.endereco}
                    onChange={(e) =>
                      setFormData({ ...formData, endereco: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Rua, número, bairro"
                  />
                </div>

                <div>
                  <label
                    htmlFor="cep"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    CEP
                  </label>
                  <input
                    type="text"
                    id="cep"
                    value={formData.cep}
                    onChange={(e) =>
                      setFormData({ ...formData, cep: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="00000-000"
                  />
                </div>

                <div>
                  <label
                    htmlFor="cidade"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Cidade
                  </label>
                  <input
                    type="text"
                    id="cidade"
                    value={formData.cidade}
                    onChange={(e) =>
                      setFormData({ ...formData, cidade: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Sua cidade"
                  />
                </div>

                <div>
                  <label
                    htmlFor="estado"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Estado
                  </label>
                  <input
                    type="text"
                    id="estado"
                    value={formData.estado}
                    onChange={(e) =>
                      setFormData({ ...formData, estado: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="UF"
                  />
                </div>
              </div>
            </div>

            {/* Foto */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-600" />
                Foto (Opcional)
              </h3>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                {previewFoto ? (
                  <div className="space-y-3">
                    <img
                      src={previewFoto}
                      alt="Preview da foto"
                      className="w-32 h-32 object-cover rounded-lg mx-auto"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFoto(null);
                        setPreviewFoto(null);
                        const fileInput = document.getElementById(
                          "foto",
                        ) as HTMLInputElement;
                        if (fileInput) fileInput.value = "";
                      }}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Remover Foto
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <div>
                      <label htmlFor="foto" className="cursor-pointer">
                        <span className="text-blue-600 hover:text-blue-700 font-medium">
                          Clique para selecionar uma foto
                        </span>
                        <p className="text-sm text-gray-500 mt-1">
                          JPG, PNG até 5MB
                        </p>
                      </label>
                      <input
                        id="foto"
                        type="file"
                        accept="image/*"
                        onChange={handleFotoChange}
                        className="hidden"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Observações */}
            <div className="pb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Observações
              </h3>

              <div>
                <label
                  htmlFor="observacoes"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Observações Adicionais
                </label>
                <textarea
                  id="observacoes"
                  rows={4}
                  value={formData.observacoes}
                  onChange={(e) =>
                    setFormData({ ...formData, observacoes: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Informações adicionais que gostaria de compartilhar..."
                />
              </div>
            </div>

            {/* Mensagem de erro */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <p className="text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Botão de envio */}
            <div className="flex justify-center">
              <button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Cadastrando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Cadastrar como Candidato
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Informações adicionais */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">* Campos obrigatórios</p>
          <p className="text-sm text-gray-500 mt-2">
            Seus dados serão utilizados exclusivamente para esta eleição e não
            serão compartilhados com terceiros.
          </p>
        </div>
      </div>

      {/* Modal de Confirmação de Candidatura */}
      {dadosConfirmacao && (
        <ConfirmacaoCandidaturaModal
          isOpen={mostrarModalConfirmacao}
          onClose={() => {
            setMostrarModalConfirmacao(false);
            setDadosConfirmacao(null);
            // Só mostra a tela de sucesso DEPOIS que o modal fecha
            // Isso garante que o usuário veja o protocolo antes de ver "Fazer Novo Cadastro"
            setSuccess(true);
          }}
          dados={{
            nome: dadosConfirmacao.nome,
            cpf: dadosConfirmacao.cpf,
            setor: dadosConfirmacao.setor,
            funcao: dadosConfirmacao.funcao,
            protocolo: dadosConfirmacao.protocolo,
            dataRegistro: dadosConfirmacao.dataRegistro,
            horaRegistro: dadosConfirmacao.horaRegistro,
            empresa: eleicaoInfo?.empresa_nome || "",
          }}
        />
      )}
    </div>
  );
};

export default CadastroCandidatoPublico;
